import React from 'react'
import {Pressable, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'

import {useTapTagsQuery} from '#/state/queries/tap-tags'
import {atoms as a, useTheme, web} from '#/alf'
import {Hashtag_Stroke2_Corner0_Rounded as HashtagIcon} from '#/components/icons/Hashtag'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

export function TagList({selectedTag}: {selectedTag?: string}) {
  const t = useTheme()
  const {_} = useLingui()
  const {data: tags, isLoading, error} = useTapTagsQuery()
  const navigation = useNavigation()

  // Sort tags alphabetically by name, excluding 'ccad' - must be called before any conditional returns
  const sortedTags = React.useMemo(() => {
    if (!tags) return []
    return [...tags]
      .filter(tag => tag.slug !== 'ccad')
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [tags])

  const handleTagPress = React.useCallback(
    (slug: string) => {
      navigation.navigate('Posts', {tag: slug})
    },
    [navigation],
  )

  if (error) {
    return (
      <View style={[a.gap_sm, {paddingBottom: 2}]}>
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          {_(msg`Unable to load tags`)}
        </Text>
      </View>
    )
  }

  if (isLoading) {
    return (
      <View style={[a.gap_sm, {paddingBottom: 2}]}>
        <View style={[a.flex_row, a.align_center, a.gap_xs]}>
          <HashtagIcon size="sm" />
          <Text
            style={[
              a.flex_1,
              a.text_sm,
              a.font_bold,
              t.atoms.text_contrast_medium,
            ]}>
            <Trans>Tags</Trans>
          </Text>
        </View>
        <View style={[a.align_center, {paddingVertical: 12}]}>
          <Loader size="lg" />
        </View>
      </View>
    )
  }

  if (!tags || tags.length === 0) {
    return null
  }

  return (
    <View style={[a.gap_sm, {paddingBottom: 2}]}>
      <View style={[a.flex_row, a.align_center, a.gap_xs]}>
        <HashtagIcon size="sm" />
        <Text
          style={[
            a.flex_1,
            a.text_sm,
            a.font_bold,
            t.atoms.text_contrast_medium,
          ]}>
          <Trans>Tags</Trans>
        </Text>
      </View>

      <View style={[a.gap_xs]}>
        {sortedTags.map(tag => {
          const isSelected = selectedTag === tag.slug
          return (
            <Pressable
              key={tag.id}
              onPress={() => handleTagPress(tag.slug)}
              accessibilityRole="button"
              accessibilityLabel={_(msg`View posts tagged with ${tag.name}`)}
              style={[
                a.flex_row,
                a.align_center,
                a.gap_sm,
                a.px_md,
                a.py_sm,
                a.rounded_sm,
                isSelected && t.atoms.bg_contrast_25,
                !isSelected &&
                  web({
                    cursor: 'pointer',
                    ':hover': {
                      backgroundColor: t.atoms.bg_contrast_25.backgroundColor,
                    },
                  }),
              ]}>
              <View
                style={[
                  {
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: tag.color || '#999',
                  },
                ]}
              />
              <Text
                style={[
                  a.text_sm,
                  a.flex_1,
                  isSelected
                    ? [a.font_bold, t.atoms.text]
                    : t.atoms.text_contrast_medium,
                ]}>
                {tag.name}
              </Text>
            </Pressable>
          )
        })}
      </View>
    </View>
  )
}
